import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScrollBar from 'react-custom-scrollbars';
import device from 'current-device';
import CommonDialog from './common/commonModal';
import { isMobileDevice, getContentTypeMapping } from '../commonUtil/util';
import { isTablet, isMobile } from '../commonUtil/responsiveUtil';
import {
    withLinkStore, ACTION_TYPES_IS_SKIP_SPLASH_SCREEN,
    ACTION_TYPES_IS_ONLY_FUNCTIONAL_COOKIES,
    ACTION_TYPES_UPDATE_DEFAULT_PRIVACY_URL,
    ACTION_TYPES_IS_STRICTLY_NECESSARY_COOKIES
} from '../app/reducers';
import RadioButton from './common/radioButton';
import { MY_PRIVACY_SETTING_VALUE, ConsentGDPRCookieValue } from '../commonUtil/constants';
import { setGDPRCookie, setUniqueInteractionCookie, getGDPRCookie, FUNCTIONAL_COOKIE_AGE, STRICTLY_NECESSARY_COOKIE_AGE } from '../commonUtil/cookieUtil';
import ApplicationEntity from '../commonUtil/appEntity';
import { createSessionBusiness, optInSessionBusiness, optOutSessionBusiness } from '../commonUtil/sessionBusiness';
import handleGotoPage from '../commonUtil/gotoPageHandler';
import DumpPrivacyLink from '../dumpComponent/dumpPrivacyLink';

const getModalStyle = () => {
    let shareDialogStyle = {
        content: {
            width: '800px',
            height: 'auto',
            borderRadius: '8px',
            maxHeight: '100vh'
        }
    };

    if (isMobileDevice.any() && isTablet()) {
        const content = {
            width: '600px',
            height: 'auto',
            borderRadius: '8px',
            maxHeight: '100vh'
        };

        shareDialogStyle = {
            ...shareDialogStyle,
            content
        };
    }

    if (isMobileDevice.any() && isMobile()) {
        const content = {
            width: '100%',
            height: '100%',
            borderRadius: '0'
        };

        shareDialogStyle = {
            ...shareDialogStyle,
            content
        };
    }

    return shareDialogStyle;
};

const MyPrivacySettings = (props) => {
    const { t } = useTranslation();
    const { isOpen, setIsOpen, linkStore, linkDispatch, showSave } = props;
    const { universalPlayerEvent, viewerSetting, contents } = linkStore;
    const { brandingHilightColor } = viewerSetting;
    const { privacyLink } = linkStore.gdprSetting;
    const { isExpired } = linkStore.linkBasicInfo;

    const defaultRadioValueHandle = () => {
        const gdprCookie = getGDPRCookie();
        if (gdprCookie === ConsentGDPRCookieValue.ALL_TRACKING_AND_COOKIES) {
            return MY_PRIVACY_SETTING_VALUE.ALL_TRACKING_AND_COOKIES;
        } else if (gdprCookie === ConsentGDPRCookieValue.ONLY_FUNCTIONAL_COOKIES) {
            return MY_PRIVACY_SETTING_VALUE.ONLY_FUNCTIONAL_COOKIES;
        }

        return MY_PRIVACY_SETTING_VALUE.STRICTLY_NECESSARY_COOKIES;
    };

    const defaultRadioValue = defaultRadioValueHandle();
    const [radioValue, setRadioValue] = useState(defaultRadioValue);

    const onRadioChanged = (e) => {
        setRadioValue(e.currentTarget.value);
    };

    const closeModal = () => {
        setIsOpen(false);
    };

    const afterCloseModal = () => {
        const currentRadioValue = defaultRadioValueHandle();
        setRadioValue(currentRadioValue);
    };

    const setIsOnlyFunctionalCookie = (value) => {
        linkDispatch({
            type: ACTION_TYPES_IS_ONLY_FUNCTIONAL_COOKIES,
            payload: value
        });
    };

    const setIsStrictlyNecessaryCookies = (value) => {
        linkDispatch({
            type: ACTION_TYPES_IS_STRICTLY_NECESSARY_COOKIES,
            payload: value
        });
    };

    const acceptPrivacySettingsHanlder = () => {
        if (radioValue === MY_PRIVACY_SETTING_VALUE.ALL_TRACKING_AND_COOKIES) {
            setIsStrictlyNecessaryCookies(false);
            setIsOnlyFunctionalCookie(false);
            setGDPRCookie();
        } else if (radioValue === MY_PRIVACY_SETTING_VALUE.STRICTLY_NECESSARY_COOKIES) {
            setIsStrictlyNecessaryCookies(true);
            setIsOnlyFunctionalCookie(false);
            setGDPRCookie(ConsentGDPRCookieValue.STRICTLY_NECESSARY_COOKIES, STRICTLY_NECESSARY_COOKIE_AGE);
        } else {
            setIsStrictlyNecessaryCookies(false);
            setIsOnlyFunctionalCookie(true);
            setGDPRCookie(ConsentGDPRCookieValue.ONLY_FUNCTIONAL_COOKIES, FUNCTIONAL_COOKIE_AGE);
        }

        setUniqueInteractionCookie();
        linkDispatch({ type: ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, payload: true });
    };

    const updateSelectPrivacyLink = (result) => {
        linkDispatch({ type: ACTION_TYPES_UPDATE_DEFAULT_PRIVACY_URL, payload: result });
    };

    const optInHandler = async () => {
        if (!ApplicationEntity.getInstance().sessionId) {
            const [, sessionId] = await createSessionBusiness(isExpired, false, false, linkDispatch);
            if (sessionId) {
                ApplicationEntity.getInstance().sessionId = sessionId;
            }
        } else {
            await optInSessionBusiness(ApplicationEntity.getInstance().tenant, ApplicationEntity.getInstance().sessionId);
        }

        if (ApplicationEntity.getInstance().sessionId) {
            handleGotoPage(universalPlayerEvent, { isExpired, isOnlyFunctionalCookies: false });
        }
    };

    const optOutHandler = async () => {
        if (ApplicationEntity.getInstance().sessionId) {
            await optOutSessionBusiness(ApplicationEntity.getInstance().tenant, ApplicationEntity.getInstance().sessionId);
        }
    };

    const savePrivacySettingHandler = () => {
        if (radioValue === MY_PRIVACY_SETTING_VALUE.ALL_TRACKING_AND_COOKIES) {
            setIsOnlyFunctionalCookie(false);
            setIsStrictlyNecessaryCookies(false);
            setGDPRCookie();
            optInHandler();
        } else if (radioValue === MY_PRIVACY_SETTING_VALUE.STRICTLY_NECESSARY_COOKIES) {
            setIsOnlyFunctionalCookie(false);
            setIsStrictlyNecessaryCookies(true);
            setGDPRCookie(ConsentGDPRCookieValue.STRICTLY_NECESSARY_COOKIES, STRICTLY_NECESSARY_COOKIE_AGE);
            optOutHandler();
        } else {
            setIsOnlyFunctionalCookie(true);
            setIsStrictlyNecessaryCookies(false);
            setGDPRCookie(ConsentGDPRCookieValue.ONLY_FUNCTIONAL_COOKIES, FUNCTIONAL_COOKIE_AGE);
            optOutHandler();
        }

        closeModal();
    };

    const deviceHeight = isMobileDevice.any() && isMobile() ? `${window.innerHeight}px` : '100vh';
    // 120、112:The sum of the height of the head and foot of the dialog.
    const computeHeight = isMobileDevice.any() && isMobile() ? '120px' : '112px';

    return (
        <CommonDialog
            customStyles={getModalStyle()}
            shouldCloseOnOverlayClick={false}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            afterCloseModal={afterCloseModal}
            showCloseIcon
            className={`seismic-engagement-viewer-privacy-setting ${device.mobile() ? 'mobile-device' : ''}`}>
            <div className="seismic-engagement-viewer-privacy-setting-header">
                {t('LIVE_SEND_VIEWER_MY_PRIVACY_SETTINGS_TITLE')}
            </div>
            <ScrollBar autoHide autoHeight autoHeightMax={`calc(${deviceHeight} - ${computeHeight})`}>
                <div className="seismic-engagement-viewer-privacy-setting-content">
                    <div className="seismic-engagement-viewer-privacy-setting-description">
                        {t('LIVE_SEND_VIEWER_MY_PRIVACY_SETTINGS_HEADER_NEW')}
                        {
                            device.mobile() &&
                            <span>
                                &nbsp;
                                {t('LIVE_SEND_VIEWER_MY_PRIVACY_SETTINGS_SEE_OUR')}&nbsp;
                                <span className="privacy-link">
                                    <DumpPrivacyLink
                                        updateSelectLink={updateSelectPrivacyLink}
                                        privacyLinkUrl={privacyLink}
                                        privacyPolicyText={t('LIVE_SEND_PRIVACY_POLICY')} />
                                </span>
                            </span>
                        }
                    </div>
                    <div className="seismic-engagement-viewer-privacy-setting-options">
                        <div className="seismic-engagement-viewer-privacy-setting-options-item">
                            <div className="option">
                                <RadioButton
                                    labelMessage={t('LIVE_SEND_VIEWER_MY_PRIVACY_SETTINGS_STRICTLY_NECESSARY')}
                                    id="strictlyNecessaryCookies"
                                    name="isTrackingRadio"
                                    onChange={onRadioChanged}
                                    value={MY_PRIVACY_SETTING_VALUE.STRICTLY_NECESSARY_COOKIES}
                                    isChecked={radioValue === MY_PRIVACY_SETTING_VALUE.STRICTLY_NECESSARY_COOKIES}
                                    highlightColor={brandingHilightColor} />
                            </div>
                            <div className="desc">
                                {t('LIVE_SEND_VIEWER_MY_PRIVACY_SETTINGS_STRICTLY_NECESSARY_DESC')}
                            </div>
                        </div>
                        <div className="seismic-engagement-viewer-privacy-setting-options-item">
                            <div className="option">
                                <RadioButton
                                    labelMessage={t('LIVE_SEND_VIEWER_MY_PRIVACY_SETTINGS_NECESSARY_FUNCTIONAL')}
                                    id="onlyFunctionalCookie"
                                    name="isTrackingRadio"
                                    onChange={onRadioChanged}
                                    value={MY_PRIVACY_SETTING_VALUE.ONLY_FUNCTIONAL_COOKIES}
                                    isChecked={radioValue === MY_PRIVACY_SETTING_VALUE.ONLY_FUNCTIONAL_COOKIES}
                                    highlightColor={brandingHilightColor} />
                            </div>
                            <div className="desc">
                                {t('LIVE_SEND_VIEWER_MY_PRIVACY_SETTINGS_NECESSARY_FUNCTIONAL_DESC')}
                            </div>
                        </div>
                        <div className="seismic-engagement-viewer-privacy-setting-options-item">
                            <div className="option">
                                <RadioButton
                                    labelMessage={t('LIVE_SEND_VIEWER_MY_PRIVACY_SETTINGS_ALL_TRACKING')}
                                    id="trackingOption"
                                    name="isTrackingRadio"
                                    onChange={onRadioChanged}
                                    value={MY_PRIVACY_SETTING_VALUE.ALL_TRACKING_AND_COOKIES}
                                    isChecked={radioValue === MY_PRIVACY_SETTING_VALUE.ALL_TRACKING_AND_COOKIES}
                                    highlightColor={brandingHilightColor} />
                            </div>
                            <div className="desc">
                                {t('LIVE_SEND_VIEWER_MY_PRIVACY_SETTINGS_ALL_TRACKING_DESC')}
                            </div>
                        </div>
                    </div>
                </div>
            </ScrollBar>
            <div className="seismic-engagement-viewer-privacy-setting-footer">
                {
                    !device.mobile() &&
                    <div className="privacy-link">
                        <DumpPrivacyLink
                            updateSelectLink={updateSelectPrivacyLink}
                            privacyLinkUrl={privacyLink}
                            privacyPolicyText={t('LIVE_SEND_PRIVACY_POLICY')} />
                    </div>
                }
                <div className="button-container">
                    <button tabIndex={0} onClick={closeModal} className="enagement-buyer-btn btn-active btn-cancel" style={{ color: brandingHilightColor, borderColor: brandingHilightColor }}>
                        {t('LIVE_SEND_VIEWER_CANCEL')}
                    </button>
                    {
                        showSave ?
                            <button tabIndex={0} onClick={savePrivacySettingHandler} className="enagement-buyer-btn btn-active btn-accept" style={{ backgroundColor: brandingHilightColor }} >
                                {t('LIVE_SEND_VIEWER_MY_PRIVACY_SETTINGS_SAVE')}
                            </button> :
                            <button tabIndex={0} onClick={acceptPrivacySettingsHanlder} className="enagement-buyer-btn btn-active btn-accept" style={{ backgroundColor: brandingHilightColor }} >
                                {t('LIVE_SEND_VIEWER_ACCEPT_AND_VIEW_CONTENT_TYPE_PRIVACY_SETTING', { content: getContentTypeMapping(t, contents, false).toLowerCase() })}
                            </button>

                    }
                </div>
            </div>
        </CommonDialog>
    );
};

export default withLinkStore(MyPrivacySettings);
